// src/services/appwriteService.ts
/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
import { Client, Databases, Account, Query, ID, Permission, Role } from 'appwrite';

const appwriteEndpoint = process.env.REACT_APP_APPWRITE_ENDPOINT || 'https://appwrite.dat-articles.com/v1';
const appwriteProjectId = process.env.REACT_APP_APPWRITE_PROJECT_ID || 'audit-focal';
const appwriteDatabaseId = process.env.REACT_APP_APPWRITE_DATABASE_ID || 'focal-db';

// ✅ Client Appwrite
export const client = new Client()
  .setEndpoint(appwriteEndpoint)
  .setProject(appwriteProjectId);

export const account = new Account(client);
export const databases = new Databases(client);

// ✅ Types corrigés avec & pour intersection
export interface AuditSession {
  $id?: string;
  auditor_id: string;
  agent_email: string;
  audit_date: string;
  total_tasks: number;
  completed_tasks: number;
  approved_tasks: number;
  rejected_tasks: number;
  session_status: 'in_progress' | 'completed' | 'paused';
  session_type?: string;
  $createdAt?: string;
  $updatedAt?: string;
}

export interface AuditTask {
  $id?: string;
  session_id: string;
  match_task_id: number;
  agent_email: string;
  task_date: string;
  task_state: string;
  priority_names?: string;
  issue_names?: string;
  processing_time_seconds: number;
  template_unpaired: number;
  template_total: number;
  plano_unpaired: number;
  plano_total: number;
  template_image_url?: string;
  plano_image_url?: string;
  model_template_url?: string;
  agent_submitted_url?: string;
  task_type: string;
  audit_status: 'pending' | 'approved' | 'rejected';
  audit_comment?: string;
  audited_by?: string;
  audited_at?: string;
  audit_date_mg?: string;
  $createdAt?: string;
  $updatedAt?: string;
}

export interface ImageComment {
  $id?: string;
  task_id: string;
  comment: string;
  severity: 'minor' | 'major' | 'critical';
  $createdAt?: string;
  $updatedAt?: string;
}

export interface UserProfile {
  $id?: string;
  email: string;
  full_name: string;
  role: 'admin' | 'auditor';
  is_active: boolean;
  $createdAt?: string;
  $updatedAt?: string;
}

// ✅ Service de gestion des sessions d'audit
export const auditSessionService = {
  // Créer une session
  createSession: async (auditorId: string, agentEmail: string, auditDate: string): Promise<AuditSession> => {
    try {
      const sessionData = {
        auditor_id: auditorId,
        agent_email: agentEmail,
        audit_date: auditDate,
        total_tasks: 0,
        completed_tasks: 0,
        approved_tasks: 0,
        rejected_tasks: 0,
        session_status: 'in_progress' as const
      };

      const response = await databases.createDocument(
        appwriteDatabaseId,
        'audit_sessions',
        ID.unique(),
        sessionData
      );

      return response as AuditSession;
    } catch (error) {
      console.error('Erreur création session:', error);
      throw error;
    }
  },

  // Obtenir les sessions d'un auditeur
  getAuditorSessions: async (auditorId: string): Promise<AuditSession[]> => {
    try {
      const response = await databases.listDocuments(
        appwriteDatabaseId,
        'audit_sessions',
        [
          Query.equal('auditor_id', auditorId),
          Query.orderDesc('$createdAt')
        ]
      );

      return response.documents as AuditSession[];
    } catch (error) {
      console.error('Erreur récupération sessions:', error);
      throw error;
    }
  },

  // Obtenir toutes les sessions
  getAllSessions: async (): Promise<AuditSession[]> => {
    try {
      const response = await databases.listDocuments(
        appwriteDatabaseId,
        'audit_sessions',
        [Query.orderDesc('$createdAt')]
      );

      return response.documents as AuditSession[];
    } catch (error) {
      console.error('Erreur récupération toutes sessions:', error);
      throw error;
    }
  }
};

// ✅ Service de gestion des tâches d'audit
export const auditTaskService = {
  // Créer une tâche
  createTask: async (taskData: Omit<AuditTask, '$id' | '$createdAt' | '$updatedAt'>): Promise<AuditTask> => {
    try {
      const response = await databases.createDocument(
        appwriteDatabaseId,
        'audit_tasks',
        ID.unique(),
        taskData
      );

      return response as AuditTask;
    } catch (error) {
      console.error('Erreur création tâche:', error);
      throw error;
    }
  },

  // Obtenir les tâches d'une session
  getSessionTasks: async (sessionId: string): Promise<AuditTask[]> => {
    try {
      const response = await databases.listDocuments(
        appwriteDatabaseId,
        'audit_tasks',
        [
          Query.equal('session_id', sessionId),
          Query.orderAsc('$createdAt')
        ]
      );

      return response.documents as AuditTask[];
    } catch (error) {
      console.error('Erreur récupération tâches session:', error);
      throw error;
    }
  },

  // Mettre à jour une tâche
  updateTask: async (taskId: string, updates: Partial<AuditTask>): Promise<AuditTask> => {
    try {
      // Filtrer les champs système
      const { $id, $createdAt, $updatedAt, ...cleanUpdates } = updates;
      
      const response = await databases.updateDocument(
        appwriteDatabaseId,
        'audit_tasks',
        taskId,
        cleanUpdates
      );

      return response as AuditTask;
    } catch (error) {
      console.error('Erreur mise à jour tâche:', error);
      throw error;
    }
  },

  // Obtenir toutes les tâches
  getAllTasks: async (): Promise<AuditTask[]> => {
    try {
      const response = await databases.listDocuments(
        appwriteDatabaseId,
        'audit_tasks',
        [Query.orderDesc('$createdAt')]
      );

      return response.documents as AuditTask[];
    } catch (error) {
      console.error('Erreur récupération toutes tâches:', error);
      throw error;
    }
  }
};

// ✅ Service de gestion des commentaires
export const imageCommentService = {
  // Créer un commentaire
  createComment: async (commentData: Omit<ImageComment, '$id' | '$createdAt' | '$updatedAt'>): Promise<ImageComment> => {
    try {
      const response = await databases.createDocument(
        appwriteDatabaseId,
        'image_comments',
        ID.unique(),
        commentData
      );

      return response as ImageComment;
    } catch (error) {
      console.error('Erreur création commentaire:', error);
      throw error;
    }
  },

  // Obtenir les commentaires d'une tâche
  getTaskComments: async (taskId: string): Promise<ImageComment[]> => {
    try {
      const response = await databases.listDocuments(
        appwriteDatabaseId,
        'image_comments',
        [
          Query.equal('task_id', taskId),
          Query.orderDesc('$createdAt')
        ]
      );

      return response.documents as ImageComment[];
    } catch (error) {
      console.error('Erreur récupération commentaires:', error);
      throw error;
    }
  },

  // Obtenir tous les commentaires
  getAllComments: async (): Promise<ImageComment[]> => {
    try {
      const response = await databases.listDocuments(
        appwriteDatabaseId,
        'image_comments',
        [Query.orderDesc('$createdAt')]
      );

      return response.documents as ImageComment[];
    } catch (error) {
      console.error('Erreur récupération tous commentaires:', error);
      throw error;
    }
  }
};

// ✅ Service d'authentification corrigé
export const authService = {
  // Connexion avec email/mot de passe
  signIn: async (email: string, password: string) => {
    try {
      // ✅ CORRECTION - Utiliser createEmailPasswordSession au lieu de createEmailSession
      const session = await account.createEmailPasswordSession(email, password);
      
      // Récupérer le profil utilisateur
      const user = await account.get();
      const profile = await databases.getDocument(
        appwriteDatabaseId,
        'users',
        user.$id
      );

      return {
        session,
        user,
        profile: profile as UserProfile
      };
    } catch (error) {
      console.error('Erreur connexion:', error);
      throw error;
    }
  },

  // Obtenir l'utilisateur actuel
  getCurrentUser: async () => {
    try {
      const user = await account.get();
      const profile = await databases.getDocument(
        appwriteDatabaseId,
        'users',
        user.$id
      );

      return {
        user,
        profile: profile as UserProfile
      };
    } catch (error) {
      return null;
    }
  },

  // Déconnexion
  signOut: async () => {
    try {
      await account.deleteSession('current');
    } catch (error) {
      console.error('Erreur déconnexion:', error);
      throw error;
    }
  },

  // Créer un utilisateur
  createUser: async (email: string, password: string, name: string): Promise<any> => {
    try {
      console.log('📝 Création utilisateur Appwrite...', { email, name });
      
      // Créer le compte utilisateur
      const user = await account.create(ID.unique(), email, password, name);
      console.log('👤 Compte créé:', user);
      
      // Créer le profil utilisateur dans la DB
      const profileData = {
        email,
        full_name: name,
        role: 'auditor' as const,
        is_active: true
      };
  
      const profile = await databases.createDocument(
        appwriteDatabaseId,
        'users',
        user.$id,
        profileData
      );
      
      console.log('📄 Profil créé:', profile);
      return profile as any;
    } catch (error) {
      console.error('❌ Erreur création utilisateur:', error);
      throw error;
    }
  }
};

// ✅ Service de gestion des utilisateurs
export const auditorService = {
  // Obtenir tous les auditeurs
  getAllAuditors: async (): Promise<UserProfile[]> => {
    try {
      const response = await databases.listDocuments(
        appwriteDatabaseId,
        'users',
        [
          Query.equal('role', 'auditor'),
          Query.orderDesc('$createdAt')
        ]
      );

      return response.documents as UserProfile[];
    } catch (error) {
      console.error('Erreur récupération auditeurs:', error);
      throw error;
    }
  },

  // Mettre à jour un auditeur
  updateAuditor: async (auditorId: string, updates: Partial<UserProfile>): Promise<UserProfile> => {
    try {
      // Filtrer les champs système
      const { $id, $createdAt, $updatedAt, ...cleanUpdates } = updates;
      
      const response = await databases.updateDocument(
        appwriteDatabaseId,
        'users',
        auditorId,
        cleanUpdates
      );

      return response as UserProfile;
    } catch (error) {
      console.error('Erreur mise à jour auditeur:', error);
      throw error;
    }
  }
};

// ✅ Service de statistiques
export const statsService = {
  // Obtenir les stats d'un auditeur
  getAuditorStats: async (auditorId: string) => {
    try {
      const [sessions, tasks] = await Promise.all([
        auditSessionService.getAuditorSessions(auditorId),
        databases.listDocuments(
          appwriteDatabaseId,
          'audit_tasks',
          [Query.equal('audited_by', auditorId)]
        )
      ]);

      const sessionsList = sessions;
      const tasksList = tasks.documents as AuditTask[];

      return {
        totalSessions: sessionsList.length,
        completedSessions: sessionsList.filter(s => s.session_status === 'completed').length,
        totalTasks: tasksList.length,
        approvedTasks: tasksList.filter(t => t.audit_status === 'approved').length,
        rejectedTasks: tasksList.filter(t => t.audit_status === 'rejected').length
      };
    } catch (error) {
      console.error('Erreur stats auditeur:', error);
      throw error;
    }
  },

  // Obtenir les stats globales
  getGlobalStats: async () => {
    try {
      const [sessionsResponse, tasksResponse] = await Promise.all([
        databases.listDocuments(appwriteDatabaseId, 'audit_sessions', [Query.limit(1)]),
        databases.listDocuments(appwriteDatabaseId, 'audit_tasks', [Query.limit(1)])
      ]);

      return {
        totalSessions: sessionsResponse.total,
        totalTasks: tasksResponse.total
      };
    } catch (error) {
      console.error('Erreur stats globales:', error);
      throw error;
    }
  }
};

export default {
  client,
  account,
  databases,
  auditSessionService,
  auditTaskService,
  imageCommentService,
  authService,
  auditorService,
  statsService
};